const DEFAULT_VENDING_GAME_ID_LENGTH = 4;
const MAX_VENDING_PACK_ID_LENGTH = 7;

const digitsOnly = (value: unknown): string => String(value ?? '').replace(/\D/g, '');

const normalizeGameIdLength = (length: unknown): number => {
  const parsedLength = Number(length);
  return Number.isFinite(parsedLength) && parsedLength > 0
    ? parsedLength
    : DEFAULT_VENDING_GAME_ID_LENGTH;
};

export const getVendingInventoryGameIdLength = (
  gamesData: Map<string, number> | null,
): number => {
  const firstGameId = gamesData?.keys().next().value;
  const firstGameIdLength = digitsOnly(firstGameId).length;

  return firstGameIdLength || DEFAULT_VENDING_GAME_ID_LENGTH;
};

export const buildVendingInventoryGamePackValue = (
  game: unknown,
  packId: unknown,
  gameIdLength: number = DEFAULT_VENDING_GAME_ID_LENGTH,
): string => {
  const gamePart = game === null || game === undefined ? '' : String(game).trim();
  const packPart = packId === null || packId === undefined || packId === ''
    ? ''
    : String(packId).trim();
  const normalizedGameIdLength = normalizeGameIdLength(gameIdLength);

  if (!gamePart) {
    return '';
  }

  if (packPart) {
    return `${gamePart}-${packPart}`;
  }

  return digitsOnly(gamePart).length === normalizedGameIdLength
    ? `${gamePart}-`
    : gamePart;
};

export const buildLegacyVendingInventoryGamePackValue = (
  game: unknown,
  packId: unknown,
): string => {
  const gamePart = game === null || game === undefined ? '' : String(game).trim();
  const packPart = packId === null || packId === undefined || packId === ''
    ? ''
    : String(packId).trim();

  return gamePart ? `${gamePart}-${packPart}` : '';
};

export const parseVendingInventoryGamePackInput = (
  rawValue: string,
  gameIdLength: number,
): { game: string; packId: string | null } => {
  const normalizedGameIdLength = normalizeGameIdLength(gameIdLength);
  const cleanValue = String(rawValue ?? '').replace(/[^\d-]/g, '');

  if (cleanValue.includes('-')) {
    const [gameSource, ...packSources] = cleanValue.split('-');
    const packId = digitsOnly(packSources.join('')).slice(0, MAX_VENDING_PACK_ID_LENGTH);

    return {
      game: digitsOnly(gameSource).slice(0, normalizedGameIdLength),
      packId: packId || null,
    };
  }

  const digits = digitsOnly(cleanValue);
  if (digits.length <= normalizedGameIdLength) {
    return {
      game: digits,
      packId: null,
    };
  }

  return {
    game: digits.slice(0, normalizedGameIdLength),
    packId: digits.slice(
      normalizedGameIdLength,
      normalizedGameIdLength + MAX_VENDING_PACK_ID_LENGTH,
    ) || null,
  };
};

export const parseLegacyVendingInventoryGamePackInput = (
  rawValue: string,
): { game: string; packId: string | null } => {
  const cleanValue = String(rawValue ?? '').replace(/[^\d-]/g, '');
  const parts = cleanValue.split('-');
  const game = digitsOnly(parts[0] || '').slice(0, 6);
  const packId = digitsOnly(parts[1] || '').slice(0, MAX_VENDING_PACK_ID_LENGTH);

  return {
    game,
    packId: packId || null,
  };
};

export const getVendingInventoryGameAfterAutoDashBackspace = (
  displayValue: string,
  gameIdLength: number,
): string | null => {
  const normalizedGameIdLength = normalizeGameIdLength(gameIdLength);
  const value = String(displayValue ?? '');

  if (!value.endsWith('-')) {
    return null;
  }

  const game = digitsOnly(value.slice(0, -1));
  if (game.length !== normalizedGameIdLength) {
    return null;
  }

  return game.slice(0, -1);
};
